import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useHistory, useLocation } from 'react-router-dom';
import { IPD_COLORS, thinScrollbarSx } from '../theme';

/**
 * Stage shape: { label, path, disabled? }
 *
 * A `disabled` stage is dimmed and inert, not dropped — the workflow still has that
 * step, this user just may not perform it. Removing it would renumber the rest and
 * misrepresent the process.
 */
export default function WorkflowPipeline({ stages }) {
  const history = useHistory();
  const location = useLocation();
  const currentPath = location.pathname;

  const activeIndex = stages.findIndex(s => currentPath === s.path || currentPath.startsWith(`${s.path}/`));

  return (
    <Box sx={{
      position: 'relative',
      mx: -0.5,
      // The strip scrolls; fade the right edge so it reads as "there is more".
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 8,
        width: 32,
        pointerEvents: 'none',
        background: `linear-gradient(90deg, transparent, ${IPD_COLORS.cardBg})`,
      },
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0,
        overflowX: 'auto',
        pb: 1,
        ...thinScrollbarSx,
      }}>
        {stages.map((stage, i) => {
          const isActive = i === activeIndex;
          const isCompleted = activeIndex >= 0 && i < activeIndex;
          const isLast = i === stages.length - 1;
          const isDisabled = Boolean(stage.disabled);

          return (
            <Box
              key={stage.label}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                flexShrink: 0,
              }}
            >
              <Box
                onClick={isDisabled ? undefined : () => history.push(stage.path)}
                role={isDisabled ? undefined : 'button'}
                tabIndex={isDisabled ? undefined : 0}
                aria-disabled={isDisabled || undefined}
                onKeyDown={isDisabled ? undefined : (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    history.push(stage.path);
                  }
                }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: { xs: 72, sm: 88 },
                  cursor: isDisabled ? 'default' : 'pointer',
                  opacity: isDisabled ? 0.45 : 1,
                  transition: 'transform 0.2s ease',
                  ...(isDisabled ? {} : {
                    '&:hover': { transform: 'translateY(-2px)' },
                    '&:hover .step-circle': isActive || isCompleted ? {} : {
                      borderColor: IPD_COLORS.primary,
                      backgroundColor: 'rgba(13, 35, 63, 0.06)',
                    },
                  }),
                }}
              >
                <Box
                  className="step-circle"
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `2px solid ${isActive || isCompleted ? IPD_COLORS.primary : IPD_COLORS.border}`,
                    background: isActive || isCompleted ? IPD_COLORS.primary : IPD_COLORS.cardBg,
                    color: isActive || isCompleted ? '#FFFFFF' : IPD_COLORS.primary,
                    fontWeight: 700,
                    fontSize: 13,
                    transition: 'all 0.3s ease',
                    boxShadow: isActive ? '0 4px 12px rgba(13,35,63,0.18)' : 'none',
                    flexShrink: 0,
                  }}
                >
                  {isCompleted ? (
                    <CheckIcon sx={{ fontSize: 18 }} />
                  ) : (
                    i + 1
                  )}
                </Box>
                <Typography sx={{
                  mt: 1,
                  fontSize: 11,
                  fontWeight: isActive ? 700 : 600,
                  color: isActive ? IPD_COLORS.primaryDark : IPD_COLORS.textSecondary,
                  textAlign: 'center',
                  lineHeight: 1.25,
                  px: 0.5,
                  transition: 'color 0.2s ease',
                }}>
                  {stage.label}
                </Typography>
              </Box>

              {!isLast && (
                <Box sx={{
                  width: { xs: 24, sm: 32 },
                  height: 2,
                  mt: '17px',
                  flexShrink: 0,
                  background: isCompleted
                    ? IPD_COLORS.primary
                    : `linear-gradient(90deg, ${IPD_COLORS.border}, ${IPD_COLORS.border})`,
                  borderRadius: 1,
                  transition: 'background 0.3s ease',
                }} />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
